





























































































import { computed, defineComponent, ref } from '@vue/composition-api'
import { useCreateLeadForm } from '@/composables/useCreateLeadForm'
import { usePostcodeApi } from '@/composables/usePostcodeApi'
import { useCurrentData } from '@/composables/useCurrentData'
import { useGender } from '@/composables/useGender'
import { ContactAPI } from '@/api/backend-client'
import { Contact } from '@/api/model/contact'
import { ReferralAPI } from '@/api/backend-client'
import { Referral } from '@/api/model/referral'
import IconArrowDoubleRight from '@/components/icons/IconArrowDoubleRight.vue'

export default defineComponent({
    name: 'CreateLeadFormStep3',
    components: { IconArrowDoubleRight },
    setup() {
        const { state } = useCreateLeadForm()
        const { currentCompany } = useCurrentData()
        const { genderOptions } = useGender()
        const contact = computed<Contact>(() => state.referral.contact as Contact)

        const fetchingExistingContacts = ref(false)
        const showExistingContacts = ref(false)
        const existingContactsByAddress = ref<Contact[]>([])
        const existingContactsByEmail = ref<Contact[]>([])

        async function fetchExistingContacts(params: any = {}) {
            const contacts = new Map<string, Contact>()

            // Fetch contacts
            const contactParams = Object.assign({}, params)
            contactParams.company = currentCompany.value.id

            try {
                const result = (await ContactAPI.getPaged(contactParams)).collection
                result.forEach(existingContact => {
                    contacts.set(existingContact.id, existingContact)
                })
            } catch (error) {
                // Ignore
            }

            // Fetch contact referrals
            const referralParams = Object.assign({})
            referralParams.company = currentCompany.value.id

            for (const param of Object.keys(params)) {
                referralParams[`contact.${param}`] = params[param]
            }

            try {
                const result = (await ReferralAPI.getPaged(referralParams)).collection
                result.forEach(referral => {
                    let existingContact = referral.contact
                    if (contacts.has(existingContact.id)) {
                        existingContact = contacts.get(existingContact.id)
                    } else {
                        contacts.set(existingContact.id, existingContact)
                    }

                    existingContact.referrals.push(referral)
                })
            } catch (error) {
                // Ignore
            }

            // Sort contact referrals (most recently updated first)
            contacts.forEach(existingContact => {
                existingContact.referrals = existingContact.referrals.sort((a: Referral, b: Referral) => {
                    const aUpdatedAt = new Date(a.updatedAt)
                    const bUpdatedAt = new Date(b.updatedAt)

                    return bUpdatedAt.getTime() - aUpdatedAt.getTime()
                })
            })

            // Return
            fetchingExistingContacts.value = false
            showExistingContacts.value = true

            return Array.from(contacts.values())
        }

        const existingContacts = computed<Contact[]>(() => {
            const contactsMap = new Map<string, Contact>()
            existingContactsByAddress.value.forEach(existingContact => contactsMap.set(existingContact.id, existingContact as Contact))
            existingContactsByEmail.value.forEach(existingContact => contactsMap.set(existingContact.id, existingContact as Contact))

            const contacts = Array.from(contactsMap.values())
            contacts.sort((a, b) => {
                const aByAddress = existingContactsByAddress.value.findIndex(existingContact => existingContact.id === a.id) >= 0
                const aByEmail = existingContactsByEmail.value.findIndex(existingContact => existingContact.id === a.id) >= 0
                const aByAddressAndEmail = aByAddress && aByEmail

                const bByAddress = existingContactsByAddress.value.findIndex(existingContact => existingContact.id === b.id) >= 0
                const bByEmail = existingContactsByEmail.value.findIndex(existingContact => existingContact.id === b.id) >= 0
                const bByAddressAndEmail = bByAddress && bByEmail

                // Display contacts found by address ánd e-mail first
                if (aByAddressAndEmail && !bByAddressAndEmail) {
                    return -1
                } else if (!aByAddressAndEmail && bByAddressAndEmail) {
                    return 1
                }

                // Display contacts with referrals before contacts without referrals
                if (a.referrals.length > 0 && b.referrals.length === 0) {
                    return -1
                } else if (a.referrals.length === 0 && b.referrals.length > 0) {
                    return 1
                }

                // Sort contacts with referrals by most recently updated referral
                if (a.referrals.length > 0 && b.referrals.length > 0) {
                    const aUpdatedAt = new Date(a.referrals[0].updatedAt)
                    const bUpdatedAt = new Date(b.referrals[0].updatedAt)

                    return bUpdatedAt.getTime() - aUpdatedAt.getTime()
                }

                // Sort contacts without referrals by name
                return a.name.localeCompare(b.name)
            })

            return contacts
        })

        function selectExistingContact(existingContact: Contact) {
            state.updateExistingContact = true
            state.referral.contact = existingContact
            state.referral.contact.zipcode = (state.referral.contact.zipcode || '').replace(' ', '')
            state.referral.contact.spouseZipcode = (state.referral.contact.spouseZipcode || '').replace(' ', '')
            showExistingContacts.value = false
        }

        const postcodeApi = usePostcodeApi()
        const fetchingZipcodeData = computed(() => postcodeApi.loading.value)
        const validZipcodeData = computed(() => postcodeApi.hasResult.value)

        async function checkZipcode() {
            if (!contact.value.zipcode || contact.value.zipcode.length < 6 || contact.value.zipcode.length > 8) {
                postcodeApi.reset()
                return
            }

            if (!contact.value.streetnumber || contact.value.streetnumber.length === 0) {
                postcodeApi.reset()
                return
            }

            const regex = /[0-9]{4}(?:\s?[a-zA-Z]{2})?/g

            if (contact.value.zipcode.match(regex) !== null) {
                contact.value.zipcode = contact.value.zipcode.replaceAll(/\s/g, '')
            } else {
                postcodeApi.reset()
                return
            }

            await postcodeApi.search(
                contact.value.zipcode,
                contact.value.streetnumber,
            )

            if (postcodeApi.hasResult && postcodeApi.firstResult.value) {
                contact.value.zipcode = postcodeApi.firstResult.value.postcode
                contact.value.streetname = postcodeApi.firstResult.value.street
                contact.value.city = postcodeApi.firstResult.value.city?.label
                contact.value.country = 'Nederland'

                existingContactsByAddress.value = await fetchExistingContacts({
                    zipcode: contact.value.zipcode,
                    streetnumber: contact.value.streetnumber,
                })
            }
        }

        async function checkEmail() {
            if (!contact.value.email || contact.value.email.includes('@') === false) {
                return
            }

            existingContactsByEmail.value = await fetchExistingContacts({
                email: contact.value.email,
            })
        }

        function goToDetail(referral: Referral) {
            const route = this.$router.resolve({
                name: 'tasks_detail',
                params: {
                    referralId: referral.id,
                    contactId: referral.contact.id,
                    toOne: this.$currentUser.betaOptInEnabled,
                },
            })

            window.open(route.href, '_blank')
        }

        return {
            contact,
            genderOptions,

            fetchingZipcodeData,
            validZipcodeData,
            checkZipcode,

            checkEmail,

            fetchingExistingContacts,
            showExistingContacts,
            existingContacts,
            selectExistingContact,

            goToDetail,
        }
    },
})
